import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import CallsTab from './CallsTab';
import WhatsAppTab from './WhatsAppTab';
import RichTextEditor, { plainTextToHtml } from '@/components/RichTextEditor';
import EmailRecipientsField from '@/components/email/EmailRecipientsField';

function CommunicationsPage() {
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('email');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const templateEditorRef = useRef(null);

  const leadsWithEmail = useMemo(() => leads.filter(l => l.email), [leads]);
  const leadsWithPhone = useMemo(() => leads.filter(l => l.phone), [leads]);
  
  const [emailForm, setEmailForm] = useState({
    lead_id: '',
    template_id: '',
    to: [],
    subject: '',
    body: '',
    cc: '',
    bcc: '',
    campaign_name: '',
  });

  const [whatsappForm, setWhatsappForm] = useState({
    lead_id: '',
    message: '',
    template_name: '',
  });

  const [smsForm, setSmsForm] = useState({
    lead_id: '',
    message: '',
  });

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    subject: '',
    body: '',
  });

  // User signatures (auto-appended to outbound emails)
  const [defaultSignatureHtml, setDefaultSignatureHtml] = useState('');

  // Attachments (uses /email/send-with-attachments multipart path when present)
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  // ─── Campaigns tab state ─────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignEmails, setCampaignEmails] = useState({});

  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const res = await axios.get(`${API}/communications/campaigns`);
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      console.error('Failed to load campaigns', e);
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  const fetchDefaultSignature = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email/signatures`);
      const sigs = response.data.signatures || [];
      const defaultSig = sigs.find((s) => s.is_default) || sigs[0];
      if (defaultSig?.content) {
        const sigHtml = plainTextToHtml(defaultSig.content);
        setDefaultSignatureHtml(sigHtml);
        // Prefill the editor with the signature so the user sees it before sending
        setEmailForm((f) => (f.body ? f : { ...f, body: `<p><br></p><br>${sigHtml}` }));
      }
    } catch (error) {
      console.error('Error fetching signatures:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchTemplates(), fetchDefaultSignature()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads, fetchTemplates, fetchDefaultSignature]);

  useEffect(() => {
    if (activeTab === 'campaigns') fetchCampaigns();
  }, [activeTab, fetchCampaigns]);

  const toggleCampaign = async (name) => {
    if (expandedCampaign === name) {
      setExpandedCampaign(null);
      return;
    }
    setExpandedCampaign(name);
    if (campaignEmails[name]) return;
    try {
      const res = await axios.get(`${API}/communications/campaigns/${encodeURIComponent(name)}/emails`);
      setCampaignEmails((prev) => ({ ...prev, [name]: res.data.emails || [] }));
    } catch (e) {
      console.error('Failed to load campaign emails', e);
    }
  };

  // Returns body with signature appended if missing (best-effort).
  const ensureSignature = (body) => {
    if (!defaultSignatureHtml) return body;
    if (!body) return defaultSignatureHtml;
    if (body.includes(defaultSignatureHtml)) return body;
    return `${body}<br>${defaultSignatureHtml}`;
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      const tplBody = plainTextToHtml(template.body || '');
      const composed = defaultSignatureHtml ? `${tplBody}<br>${defaultSignatureHtml}` : tplBody;
      setEmailForm({
        ...emailForm,
        template_id: templateId,
        subject: template.subject,
        body: composed,
      });
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.lead_id || !emailForm.subject || !emailForm.body) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }
    if (!emailForm.to || emailForm.to.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one recipient in the To field.' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      const finalBody = ensureSignature(emailForm.body);
      if (attachments.length > 0) {
        // Multipart path — backend uses Microsoft Graph (single-call or
        // upload-session depending on size). Requires the user's M365 to be
        // connected.
        const fd = new FormData();
        fd.append('to', emailForm.to.join(','));
        fd.append('subject', emailForm.subject);
        fd.append('body', finalBody);
        if (emailForm.cc) fd.append('cc', emailForm.cc);
        if (emailForm.bcc) fd.append('bcc', emailForm.bcc);
        if (emailForm.lead_id) fd.append('lead_id', emailForm.lead_id);
        attachments.forEach((f) => fd.append('files', f));
        await axios.post(`${API}/email/send-with-attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 5 * 60 * 1000,
        });
      } else {
        await axios.post(`${API}/communications/email`, {
          lead_id: emailForm.lead_id,
          template_id: emailForm.template_id || null,
          to: emailForm.to,
          subject: emailForm.subject,
          body: finalBody,
          cc: emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()) : [],
          bcc: emailForm.bcc ? emailForm.bcc.split(',').map(e => e.trim()) : [],
        });
      }

      setMessage({ type: 'success', text: 'Email sent successfully!' });
      setEmailForm({
        lead_id: '',
        template_id: '',
        to: [],
        subject: '',
        body: defaultSignatureHtml ? `<p><br></p><br>${defaultSignatureHtml}` : '',
        cc: '',
        bcc: '',
      });
      setAttachments([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Email send error:', error);
      let errorMessage = 'Failed to send email. Check SMTP settings.';
      if (error.response?.data?.detail) {
        // Handle both string and object error details
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      }
      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = async (e) => {
    e.preventDefault();
    if (!whatsappForm.lead_id || !whatsappForm.message) {
      setMessage({ type: 'error', text: 'Please select a lead and enter message' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.post(`${API}/communications/whatsapp`, whatsappForm);
      setMessage({ type: 'success', text: 'WhatsApp message sent successfully!' });
      setWhatsappForm({ lead_id: '', message: '', template_name: '' });
    } catch (error) {
      console.error('WhatsApp send error:', error);
      let errorMessage = 'Failed to send WhatsApp. Check API settings.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      }
      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setSending(false);
    }
  };

  const sendSMS = async (e) => {
    e.preventDefault();
    if (!smsForm.lead_id || !smsForm.message) {
      setMessage({ type: 'error', text: 'Please select a lead and enter message' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      await axios.post(`${API}/communications/sms`, smsForm);
      setMessage({ type: 'success', text: 'SMS sent successfully!' });
      setSmsForm({ lead_id: '', message: '' });
    } catch (error) {
      console.error('SMS send error:', error);
      let errorMessage = 'Failed to send SMS. Check API settings.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      }
      setMessage({
        type: 'error',
        text: errorMessage,
      });
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name || !newTemplate.subject || !newTemplate.body) {
      setMessage({ type: 'error', text: 'Please fill in all template fields' });
      return;
    }

    try {
      if (editingTemplate) {
        // Update existing template
        await axios.put(`${API}/email-templates/${editingTemplate.id}`, newTemplate);
        setMessage({ type: 'success', text: 'Template updated successfully!' });
      } else {
        // Create new template
        await axios.post(`${API}/email-templates`, newTemplate);
        setMessage({ type: 'success', text: 'Template created successfully!' });
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setNewTemplate({ name: '', subject: '', body: '' });
      fetchTemplates();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save template' });
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setNewTemplate({
      name: template.name,
      subject: template.subject,
      // Legacy plain-text templates are auto-upgraded to HTML on load so the
      // rich editor displays them with proper paragraphs/bullets instead of
      // one wall of text. HTML bodies pass through unchanged.
      body: plainTextToHtml(template.body || ''),
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/email-templates/${templateId}`);
      setMessage({ type: 'success', text: 'Template deleted!' });
      fetchTemplates();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete template' });
    }
  };

  const openNewTemplateModal = () => {
    setEditingTemplate(null);
    setNewTemplate({ name: '', subject: '', body: '' });
    setShowTemplateModal(true);
  };

  const getLeadInfo = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? { name: lead.name, email: lead.email, phone: lead.phone } : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="communications-loading">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'email', label: 'Email', icon: 'mail' },
    { id: 'call', label: 'Call', icon: 'phone' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'chat' },
    { id: 'sms', label: 'SMS', icon: 'phone' },
    { id: 'campaigns', label: 'Campaigns', icon: 'tag' },
    { id: 'templates', label: 'Templates', icon: 'template' },
  ];

  return (
    <div className="space-y-6" data-testid="communications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="page-title">Communications</h1>
          <p className="page-subtitle">Send emails, WhatsApp messages, and SMS to your leads</p>
        </div>
        {activeTab === 'email' && (
          <button
            onClick={() => setShowTemplateModal(true)}
            className="px-3 sm:px-4 py-2 border border-indigo-600 text-blue-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition-all flex items-center font-medium text-sm"
            data-testid="create-template-btn"
          >
            <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Create Template</span>
            <span className="sm:hidden">Template</span>
          </button>
        )}
      </div>

      {message.text && (
        <div
          className={`px-4 py-3 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
          data-testid="communications-message"
        >
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-slate-100 dark:border-slate-700 overflow-x-auto scrollbar-hide">
          <nav className="flex space-x-1 p-2 min-w-max" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon === 'mail' && <MailIcon className="w-5 h-5 mr-2" />}
                {tab.icon === 'chat' && <ChatIcon className="w-5 h-5 mr-2" />}
                {tab.icon === 'phone' && <PhoneIcon className="w-5 h-5 mr-2" />}
                {tab.icon === 'tag' && (
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                )}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-3 sm:p-6">
          {/* Email Tab */}
          {activeTab === 'email' && (
            <form onSubmit={sendEmail} className="space-y-6" data-testid="email-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label mb-2">Select Lead *</label>
                  <select
                    value={emailForm.lead_id}
                    onChange={(e) => {
                      const newLeadId = e.target.value;
                      const lead = leadsWithEmail.find((l) => l.id === newLeadId);
                      // Auto-fill the To field with the lead's email when
                      // it's empty or still contains the previously-picked
                      // lead's email (so we never clobber a manually-typed list).
                      setEmailForm((f) => {
                        const prevLead = leadsWithEmail.find((l) => l.id === f.lead_id);
                        const next = { ...f, lead_id: newLeadId };
                        const leadEmail = (lead?.email || '').trim();
                        if (!leadEmail) return next;
                        if (!f.to || f.to.length === 0) {
                          next.to = [leadEmail];
                        } else if (
                          prevLead?.email &&
                          f.to.length === 1 &&
                          f.to[0].trim().toLowerCase() === prevLead.email.trim().toLowerCase()
                        ) {
                          next.to = [leadEmail];  // smart switch
                        } else if (!f.to.some((e) => e.toLowerCase() === leadEmail.toLowerCase())) {
                          next.to = [...f.to, leadEmail];  // append
                        }
                        return next;
                      });
                    }}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    data-testid="email-lead-select"
                  >
                    <option value="">Choose a lead...</option>
                    {leadsWithEmail.map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.name} ({lead.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label mb-2">Use Template</label>
                  <select
                    value={emailForm.template_id}
                    onChange={(e) => handleTemplateSelect(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    data-testid="email-template-select"
                  >
                    <option value="">Select template (optional)</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <EmailRecipientsField
                  label="To *"
                  value={emailForm.to}
                  onChange={(arr) => setEmailForm((f) => ({ ...f, to: arr }))}
                  placeholder="Add recipients (lead picker auto-fills, type to search more, comma to add)"
                  testIdPrefix="email-to"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Auto-filled when you pick a lead. You can add more recipients — separate with commas, Enter, or pick from the suggestions list.
                </p>
              </div>

              <div>
                <label className="form-label mb-2">Subject *</label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="Email subject"
                  data-testid="email-subject-input"
                />
              </div>

              <div>
                <label className="form-label mb-2 flex items-center gap-2">
                  Campaign / Group Tag
                  <span className="text-xs text-slate-400 dark:text-slate-500 font-normal normal-case">(optional — helps you identify this email later)</span>
                </label>
                <input
                  type="text"
                  value={emailForm.campaign_name}
                  onChange={(e) => setEmailForm({ ...emailForm, campaign_name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Q1 Outreach, Webinar Follow-up, Trade Show Mumbai 2026"
                  data-testid="email-campaign-input"
                  maxLength={120}
                />
              </div>

              <div>
                <label className="form-label mb-2">Message *</label>
                <RichTextEditor
                  value={emailForm.body}
                  onChange={(html) => setEmailForm({ ...emailForm, body: html })}
                  placeholder="Compose your email..."
                  minHeight="220px"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Tip: Use variables like <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{{lead_name}}'}</code>, <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{{company_name}}'}</code>, <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{'{{deal_value}}'}</code> to personalize your email.
                </p>
              </div>

              <div>
                <label className="form-label mb-2">Attachments</label>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 font-medium"
                    data-testid="email-attach-btn"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Add attachment
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="email-file-input"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      const MAX_SINGLE = 50 * 1024 * 1024;
                      const MAX_TOTAL = 50 * 1024 * 1024;
                      const oversized = picked.find((f) => f.size > MAX_SINGLE);
                      if (oversized) {
                        setMessage({ type: 'error', text: `"${oversized.name}" is ${(oversized.size / 1024 / 1024).toFixed(1)} MB — per-file limit is 50 MB.` });
                        e.target.value = '';
                        return;
                      }
                      const total = picked.reduce((s, f) => s + f.size, 0)
                        + attachments.reduce((s, f) => s + f.size, 0);
                      if (total > MAX_TOTAL) {
                        setMessage({ type: 'error', text: `Combined size is ${(total / 1024 / 1024).toFixed(1)} MB — please keep total under 50 MB.` });
                        e.target.value = '';
                        return;
                      }
                      setAttachments((prev) => [...prev, ...picked]);
                      e.target.value = '';
                    }}
                  />
                  {attachments.length > 0 && (
                    <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="email-attachments-summary">
                      {attachments.length} file{attachments.length === 1 ? '' : 's'} · {(attachments.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                      {attachments.reduce((s, f) => s + f.size, 0) > 3 * 1024 * 1024 && (
                        <span className="text-amber-600 dark:text-amber-400"> · large-attachment mode (chunked upload)</span>
                      )}
                    </span>
                  )}
                </div>
                {attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="email-attachments-list">
                    {attachments.map((file, idx) => (
                      <span key={`${file.name}-${idx}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="max-w-[220px] truncate" title={file.name}>{file.name}</span>
                        <span className="text-xs text-slate-400">({Math.round(file.size / 1024)} KB)</span>
                        <button
                          type="button"
                          onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-slate-400 hover:text-red-500 ml-1"
                          data-testid={`remove-attachment-${idx}`}
                          title="Remove attachment"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  Attachments are sent via Microsoft 365 (Graph). Files over 3 MB are uploaded in chunks. Max 50 MB total.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label mb-2">CC (comma separated)</label>
                  <input
                    type="text"
                    value={emailForm.cc}
                    onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="cc@example.com, another@example.com"
                    data-testid="email-cc-input"
                  />
                </div>
                <div>
                  <label className="form-label mb-2">BCC (comma separated)</label>
                  <input
                    type="text"
                    value={emailForm.bcc}
                    onChange={(e) => setEmailForm({ ...emailForm, bcc: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                    placeholder="bcc@example.com"
                    data-testid="email-bcc-input"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary disabled:opacity-50 flex items-center"
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <MailIcon className="w-5 h-5 mr-2" />
                      Send Email
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Call Tab */}
          {activeTab === 'call' && (
            <CallsTab />
          )}

          {/* WhatsApp Tab */}
          {activeTab === 'whatsapp' && (
            <WhatsAppTab />
          )}

          {/* SMS Tab */}
          {activeTab === 'sms' && (
            <form onSubmit={sendSMS} className="space-y-6" data-testid="sms-form">
              <div>
                <label className="form-label mb-2">Select Lead *</label>
                <select
                  value={smsForm.lead_id}
                  onChange={(e) => setSmsForm({ ...smsForm, lead_id: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  data-testid="sms-lead-select"
                >
                  <option value="">Choose a lead...</option>
                  {leadsWithPhone.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name} ({lead.phone})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label mb-2">Message *</label>
                <textarea
                  value={smsForm.message}
                  onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
                  rows="4"
                  maxLength={160}
                  className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="Type your SMS message..."
                  data-testid="sms-message-input"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{smsForm.message.length}/160 characters</p>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={sending}
                  className="px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium transition-all disabled:opacity-50 flex items-center"
                  data-testid="send-sms-btn"
                >
                  {sending ? 'Sending...' : (
                    <>
                      <PhoneIcon className="w-5 h-5 mr-2" />
                      Send SMS
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Campaigns Tab — every email tagged with a Campaign / Group Tag */}
          {activeTab === 'campaigns' && (
            <div className="space-y-4" data-testid="campaigns-tab">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Campaigns & Groups</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Every email you tagged with a Campaign / Group Tag, grouped together. Click a row to see the emails sent under it.
                  </p>
                </div>
                <button
                  onClick={fetchCampaigns}
                  className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  data-testid="refresh-campaigns-btn"
                >
                  Refresh
                </button>
              </div>

              {campaignsLoading ? (
                <div className="text-center py-12 text-slate-400">Loading…</div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <p className="font-medium">No campaigns yet</p>
                  <p className="text-xs mt-1">Add a "Campaign / Group Tag" when sending an email and it will show up here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                  <table className="w-full text-sm" data-testid="campaigns-table">
                    <thead className="bg-slate-50 dark:bg-slate-800/70 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Campaign / Group</th>
                        <th className="px-4 py-3 text-right font-medium">Emails Sent</th>
                        <th className="px-4 py-3 text-right font-medium">Recipients</th>
                        <th className="px-4 py-3 text-right font-medium">Opens</th>
                        <th className="px-4 py-3 text-right font-medium">Clicks</th>
                        <th className="px-4 py-3 text-left font-medium">Last Sent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                      {campaigns.map((c) => (
                        <React.Fragment key={c.campaign_name}>
                          <tr
                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => toggleCampaign(c.campaign_name)}
                            data-testid={`campaign-row-${c.campaign_name}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <svg className={`w-3 h-3 text-slate-400 transition-transform ${expandedCampaign === c.campaign_name ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                  {c.campaign_name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-white">{c.emails_sent}</td>
                            <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">{c.unique_recipient_count}</td>
                            <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">{c.open_count || 0}</td>
                            <td className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">{c.click_count || 0}</td>
                            <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                              {c.last_sent_at ? new Date(c.last_sent_at).toLocaleString() : '-'}
                            </td>
                          </tr>
                          {expandedCampaign === c.campaign_name && (
                            <tr>
                              <td colSpan={6} className="px-4 py-3 bg-slate-50 dark:bg-slate-900/40">
                                {!campaignEmails[c.campaign_name] ? (
                                  <div className="text-xs text-slate-400 py-2">Loading emails…</div>
                                ) : campaignEmails[c.campaign_name].length === 0 ? (
                                  <div className="text-xs text-slate-400 py-2">No emails found.</div>
                                ) : (
                                  <table className="w-full text-xs" data-testid={`campaign-emails-${c.campaign_name}`}>
                                    <thead className="text-slate-500 dark:text-slate-400">
                                      <tr>
                                        <th className="px-2 py-1.5 text-left font-medium">Subject</th>
                                        <th className="px-2 py-1.5 text-left font-medium">To</th>
                                        <th className="px-2 py-1.5 text-left font-medium">Sent</th>
                                        <th className="px-2 py-1.5 text-right font-medium">Opens</th>
                                        <th className="px-2 py-1.5 text-right font-medium">Clicks</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                      {campaignEmails[c.campaign_name].map((em) => (
                                        <tr key={em.id}>
                                          <td className="px-2 py-1.5 text-slate-800 dark:text-slate-200 truncate max-w-[260px]" title={em.subject}>{em.subject || '(no subject)'}</td>
                                          <td className="px-2 py-1.5 text-slate-600 dark:text-slate-400 truncate max-w-[200px]" title={em.to}>{em.to || '-'}</td>
                                          <td className="px-2 py-1.5 text-slate-500 dark:text-slate-400">{em.sent_at ? new Date(em.sent_at).toLocaleString() : '-'}</td>
                                          <td className="px-2 py-1.5 text-right text-slate-700 dark:text-slate-300">{em.open_count || 0}</td>
                                          <td className="px-2 py-1.5 text-right text-slate-700 dark:text-slate-300">{em.click_count || 0}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Templates Tab - List and Manage Templates */}
          {activeTab === 'templates' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Email Templates</h3>
                <button
                  onClick={openNewTemplateModal}
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium flex items-center gap-2"
                  data-testid="create-template-btn"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Template
                </button>
              </div>

              {/* Variables Reference */}
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Available Variables</h4>
                <div className="flex flex-wrap gap-2 text-sm">
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{lead_name}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{first_name}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{company_name}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{industry}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{email}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{phone}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{user_name}}'}</span>
                  <span className="bg-blue-100 dark:bg-blue-800/50 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">{'{{designation}}'}</span>
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">These variables will be replaced with actual data when sending emails.</p>
              </div>

              {/* Templates List */}
              {templates.length === 0 ? (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="font-medium">No templates yet</p>
                  <p className="text-sm">Create your first email template to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div key={template.id} className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg p-4 hover:shadow-md transition" data-testid={`template-card-${template.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 dark:text-white">{template.name}</h4>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1"><span className="font-medium">Subject:</span> {template.subject}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 line-clamp-2">{template.body?.replace(/<[^>]*>/g, '').substring(0, 150)}...</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="Edit Template"
                            data-testid={`edit-template-${template.id}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete Template"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-testid="template-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
              {editingTemplate ? 'Edit Email Template' : 'Create Email Template'}
            </h3>
            <form onSubmit={saveTemplate} className="space-y-4">
              <div>
                <label className="form-label mb-2">Template Name *</label>
                <input
                  type="text"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Follow-up Template"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <label className="form-label mb-2">Subject *</label>
                <input
                  type="text"
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g., Following up on our conversation, {{lead_name}}"
                  data-testid="template-subject-input"
                />
              </div>
              <div>
                <label className="form-label mb-2">Body *</label>
                <RichTextEditor
                  ref={templateEditorRef}
                  value={newTemplate.body}
                  onChange={(html) => setNewTemplate({ ...newTemplate, body: html })}
                  placeholder="Hi {{lead_name}}, Thank you for your interest..."
                  minHeight="240px"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Use the toolbar for bold, bullets, and links. Press Enter for new paragraphs — the formatting will be preserved when sent (both Gmail and Outlook render it correctly).
                </p>
              </div>
              
              {/* Template Variables Help */}
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg p-4">
                <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-2">Available Variables</h4>
                <p className="text-sm text-blue-600 mb-2">Click to insert at cursor position:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { var: '{{lead_name}}', desc: 'Lead Name' },
                    { var: '{{lead_email}}', desc: 'Lead Email' },
                    { var: '{{lead_phone}}', desc: 'Lead Phone' },
                    { var: '{{company_name}}', desc: 'Company' },
                    { var: '{{industry}}', desc: 'Industry' },
                    { var: '{{deal_value}}', desc: 'Deal Value (INR)' },
                    { var: '{{lead_status}}', desc: 'Lead Status' },
                    { var: '{{user_name}}', desc: 'Your Name' },
                  ].map((v) => (
                    <button
                      key={v.var}
                      type="button"
                      onClick={() => templateEditorRef.current?.insertText(v.var)}
                      className="px-2 py-1 text-xs bg-white dark:bg-slate-700 border border-indigo-300 dark:border-indigo-600 rounded text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30"
                      title={v.desc}
                      data-testid={`template-var-${v.var.replace(/[{}]/g, '')}`}
                    >
                      {v.var}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 btn-primary rounded-lg hover:from-indigo-700 hover:to-purple-700"
                  data-testid="save-template-btn"
                >
                  Save Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Icon Components
function MailIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function ChatIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function PhoneIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

export default CommunicationsPage;
